import { Component, effect, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { NotificationsService } from './notifications.service';
import { InlineDialogComponent } from 'app/shared/dialog/inline-dialog/inline-dialog.component';
import { MatTab, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { IconComponent } from 'app/shared/icon/icon.component';
import { DayEnum, INotification, NotificationEnum } from './model/notifications.model';
import { ButtonComponent } from 'app/shared/button/button.component';
import { heroPhoto, heroUserCircle } from '@ng-icons/heroicons/outline';
import { provideIcons } from '@ng-icons/core';
import { RouterModule } from '@angular/router';
import { routePath } from 'app/shared/app.config';
import { MatCheckbox } from '@angular/material/checkbox';

@Component({
  selector: 'app-notifications',
  imports: [
    InlineDialogComponent,
    MatTabGroup,
    MatTab,
    MatTabLabel,
    IconComponent,
    MatCheckbox,
    ButtonComponent,
    RouterModule,
  ],
  standalone: true,
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  providers: [provideIcons({ heroPhoto, heroUserCircle })],
})
export class NotificationsComponent implements OnInit {
  @ViewChild('loadMore') set loadMoreEl(el: ElementRef | undefined) {
    if (el) {
      this.observer?.observe(el.nativeElement);
    } else {
      this.observer?.disconnect();
    }
  }

  private observer!: IntersectionObserver;
  private service = inject(NotificationsService);
  NotificationEnum = NotificationEnum;
  DayEnum = DayEnum;
  routePath = routePath;

  notifications = this.service.notifications;
  total = this.service.total;
  reviewTotal = this.service.reviewTotal;
  activityTotal = this.service.activityTotal;
  open = this.service.dialogOpened;
  preHandled = this.service.preHandled;
  preRead = this.service.preRead;
  selectedTab = this.service.selectedTab;
  isEdit = this.service.isEdit;
  selectedIds = this.service.selectedIds;

  constructor() {
    effect(() => {
      if (!this.service.dialogOpened()) return;
      this.service.getNotifications({ handleable: !!this.service.selectedTab() });
    });
  }

  hasActivityButton(notification: INotification) {
    return (
      this.selectedTab() === NotificationEnum.ACTIVITY &&
      !notification.metadata.handled &&
      notification.notificationType === 'followerRequest' &&
      !this.preHandled().includes(notification.id)
    );
  }

  toggleEdit() {
    this.service.toggleEdit();
  }
  toggleId(id: string) {
    this.service.toggleId(id);
  }
  approveAll() {
    this.service.approveAll();
  }
  rejectAll() {
    this.service.rejectAll();
  }

  getNotificationLink(notification: INotification): string[] {
    if (
      notification.notificationType === 'followerRequest' ||
      notification.notificationType === 'contactRegistered' ||
      notification.notificationType === 'userContactRegister'
    ) {
      return [`/${this.routePath.friendPrefix}/${notification.metadata.userId}`];
    }
    return [`/${this.routePath.stories}/${notification.metadata.storyId}`];
  }

  ngOnInit() {
    this.observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !this.service.isLoading() && !this.service.isLast()) {
        this.service.getNotifications({
          handleable: !!this.service.selectedTab(),
          skip: this.service.notificationLength(),
        });
      }
    });
  }

  onTabIndexChange(tab: NotificationEnum) {
    this.service.selectedTab.set(tab);
    this.service.resetEdit();
  }

  notificationClicked(notification: INotification) {
    if (!notification.viewed) {
      this.service.markAsRead(notification);
    }
    this.service.toggleDialog();
  }

  share(friendId: number, notificationId: string, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.service.shareTo(friendId, notificationId);
  }

  delete(friendId: number, notificationId: string, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.service.delete(friendId, notificationId);
  }

  approve(commentId: string, notificationId: string, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.service.approve(commentId, notificationId);
  }

  reject(commentId: string, notificationId: string, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.service.reject(commentId, notificationId);
  }
}
